import currency from "currency.js";
import { useTranslation } from "react-i18next";
import { OrderTypeResponse, SideResponse } from "../../../codegen-api";
import {
  COLORS,
  LAYER_COLORS,
  TEXT_COLORS,
} from "../../../constants/design/colors";
import { FONT_SIZE } from "../../../constants/design/fontSize";
import { sideColors } from "../../../utils/sideColors";
import { AssetResponse } from "../../../utils/asset";
import { getAssetShortName } from "../../../utils/asset/assets";
import { floorSizeForDisplay } from "../../../utils/format";
import { UNKNOWN_DISPLAY } from "../../../utils/unknownNumbers";
import { floorToDecimalsString } from "../../../utils/decimalString";
import SegmentedControl from "../../shared/SegmentedControl";
import {
  Padding,
  TransactionTypeWrapper,
} from "../style";
import {
  AccountInfoLabelText,
  AccountInfoRowItem,
  AccountInfoRowsWrapper,
  AccountInfoValueButton,
  AccountInfoValueText,
  OrderTypeTabBtn,
  OrderTypeTabsWrapper,
  TopButtonsRowWrapper,
} from "./style";
import { MarginSelector } from "./form";
import { formatSpotAvailable } from "./spotBalances";
import { PerpsTradeFormCtx } from "./PerpsTradeForm.ctx";

export default function PerpsTradeFormTopSection({
  ctx,
}: {
  ctx: PerpsTradeFormCtx;
}) {
  const { t: formT } = useTranslation("app", {
    keyPrefix: "TradeForm.PerpsTradeForm.PerpsTradeForm",
  });
  const {
    isPerp,
    perpInstrument,
    form,
    orderDirection,
    commonFormTranslations,
    stopType,
    orderType,
    setStopType,
    setOrderType,
    isSpot,
    setOrderDirection,
    accountData,
    currentPosition,
    positionsKnown,
    contractPriceStep,
    spotAvailableToTrade,
    spotTokenBalances,
    sizeMax,
    setSizeMax,
  } = ctx;

  // Perp: the free margin MAX is sized from. `null` is the API's "cannot value
  // this account" and reads "—"; an API that omits the field entirely falls
  // back to available_balance, as buying power always has.
  const perpAvailable =
    accountData?.withdrawable === null
      ? null
      : accountData?.withdrawable ?? accountData?.available_balance;

  // Spot "Available to Trade" is the balance of the token you actually spend/sell — the quote
  // (USDC) on a BUY, the base (e.g. BTC) on a SELL — NOT the total portfolio value. Perp keeps the
  // cross-margin available (USD).
  const availableToTradeText = isSpot
    ? spotAvailableToTrade
      ? formatSpotAvailable(
          spotAvailableToTrade,
          contractPriceStep.amount_step,
          contractPriceStep.amount_precision
        )
      : "—"
    : perpAvailable === null || perpAvailable === undefined
      ? "— USDC"
      : `${currency(floorToDecimalsString(perpAvailable, 2), {
          precision: 2,
        }).format()} USDC`;
  // Clicking it is MAX — only when MAX would actually fill something.
  const availableIsMax = sizeMax.ok;

  const baseSymbol =
    getAssetShortName(perpInstrument?.underlying_asset as AssetResponse) ||
    perpInstrument?.underlying_asset ||
    "";
  // Spot holds a BALANCE of the base token, not a position (A1). Unknown — the
  // account has not loaded, or its row has no figure — reads "-", never 0; a
  // loaded account with no row for the token is a real 0 (spotBalances.ts).
  const spotBaseBalance = spotTokenBalances?.base;
  const spotBalanceText =
    !spotBaseBalance || spotBaseBalance.amount === null
      ? `${UNKNOWN_DISPLAY} ${spotBaseBalance?.symbol || baseSymbol}`
      : formatSpotAvailable(
          spotBaseBalance,
          contractPriceStep.amount_step,
          contractPriceStep.amount_precision
        );

  return (
    <>
      {/* ── Top 3 buttons: Margin Type | Leverage | Classic ── */}
      {isPerp && (
        <TopButtonsRowWrapper>
          <MarginSelector
            instrument={perpInstrument}
            form={form}
            side={orderDirection}
            renderAsTopBar
          />
        </TopButtonsRowWrapper>
      )}

      {/* ── Order type tabs with underline ── */}
      <OrderTypeTabsWrapper>
        <OrderTypeTabBtn
          type="button"
          $active={!stopType && orderType === OrderTypeResponse.Market}
          onClick={() => {
            setStopType(undefined);
            setOrderType(OrderTypeResponse.Market);
          }}
        >
          {commonFormTranslations("market")}
        </OrderTypeTabBtn>
        <OrderTypeTabBtn
          type="button"
          $active={!stopType && orderType === OrderTypeResponse.Limit}
          onClick={() => {
            setStopType(undefined);
            setOrderType(OrderTypeResponse.Limit);
          }}
        >
          {commonFormTranslations("limit")}
        </OrderTypeTabBtn>
      </OrderTypeTabsWrapper>

      <Padding>
        {/* ── Buy / Long | Sell / Short ── */}
        <TransactionTypeWrapper>
          <SegmentedControl
            segments={[
              {
                value: String(SideResponse.Buy),
                display: isSpot ? "Buy" : "Buy / Long",
                textColor:
                  orderDirection === SideResponse.Buy
                    ? COLORS.up.on
                    : TEXT_COLORS.three,
              },
              {
                value: String(SideResponse.Sell),
                display: isSpot ? "Sell" : "Sell / Short",
                textColor:
                  orderDirection === SideResponse.Sell
                    ? COLORS.down.on
                    : TEXT_COLORS.three,
              },
            ]}
            value={String(orderDirection)}
            onSelect={(value) => setOrderDirection(value as SideResponse)}
            config={{
              color: sideColors(orderDirection).one,
              widthType: "fullWidth",
              backgroundColor: LAYER_COLORS.two,
              activeBackgroundColor: sideColors(orderDirection).one,
              borderRadius: "10px",
              button: {
                height: 40,
                fontSize: FONT_SIZE.two,
              },
            }}
          />
        </TransactionTypeWrapper>
      </Padding>

      {/* ── Available to Trade + Current Position ── */}
      <AccountInfoRowsWrapper>
        <AccountInfoRowItem>
          <AccountInfoLabelText>Available to Trade</AccountInfoLabelText>
          {availableIsMax ? (
            <AccountInfoValueButton
              type="button"
              onClick={setSizeMax}
              title="Use the maximum order size"
            >
              {availableToTradeText}
            </AccountInfoValueButton>
          ) : (
            <AccountInfoValueText>{availableToTradeText}</AccountInfoValueText>
          )}
        </AccountInfoRowItem>
        <AccountInfoRowItem>
          <AccountInfoLabelText>
            {formT(isSpot ? "balance" : "current_position")}
          </AccountInfoLabelText>
          <AccountInfoValueText>
            {isSpot
              ? spotBalanceText
              : currentPosition
              ? `${floorSizeForDisplay(
                  currentPosition.amount,
                  contractPriceStep.amount_step,
                  contractPriceStep.amount_precision,
                  "-"
                )} ${getAssetShortName(perpInstrument?.underlying_asset as AssetResponse) || perpInstrument?.underlying_asset || ""}`
              : // No row: FLAT only when positions are known (loaded, no error,
                // account present) -- then it is a real 0. Otherwise UNKNOWN:
                // `currentPosition` is positionsMap[instrument], empty while
                // positions load and when /account 401s, and this used to print
                // `0.00 <ASSET>` there, asserting the trader holds nothing.
                `${floorSizeForDisplay(
                  positionsKnown ? "0" : undefined,
                  contractPriceStep.amount_step,
                  contractPriceStep.amount_precision,
                  UNKNOWN_DISPLAY
                )} ${getAssetShortName(perpInstrument?.underlying_asset as AssetResponse) || perpInstrument?.underlying_asset || ""}`}
          </AccountInfoValueText>
        </AccountInfoRowItem>
      </AccountInfoRowsWrapper>
    </>
  );
}
